// FILE: src/components/PaceCard.js
//
// Live Pace Indicator — the glanceable pacing card shown beside SessionTimer
// during a timed exam. Presentational and dumb: it renders a snapshot produced
// by paceService.computePace() and nothing else (no timers, no math, no I/O).
//
// Design: docs/pace-coaching-proposal.md §3 (Revision 2). It LEADS with a
// plain-language forecast of the OUTCOME the learner cares about:
//   • on track to finish → projected REVIEW time ("≈ 25 min for review")
//   • tracking to run out → projected UNANSWERED questions ("≈ 12 questions
//     unanswered at this pace")
// plus a confidence chip. The question count is intentionally omitted (the
// Question Navigator already shows answered/remaining). Quiet by design — no
// motion — and NEUTRAL during warmup, before the observed rate is trustworthy.
//
// Gating: returns null without a valid snapshot. Scope/enable gating (mode in
// appliesTo, feature enabled) is the caller's job — App.js simply doesn't
// render <PaceCard> when pacing is off.

import React from 'react';
import css from './PaceCard.module.css';

// Five confidence labels over a 4-color palette (amber shared by the two middle
// tiers — no multiple amber shades). Mirrors the proposal's §2 table.
const CONFIDENCE = {
  comfortable: { label: 'Comfortable',        color: '#2e7d32' },
  good:        { label: 'Good',               color: '#1565c0' },
  tight:       { label: 'Tight',              color: '#e07b00' },
  atRisk:      { label: 'At Risk',            color: '#e07b00' },
  unlikely:    { label: 'Unlikely to finish', color: '#c62828' },
};

/** Format a duration using hours when >= 60 min (e.g. "25 min", "1h 54m"). */
function fmtDuration(totalSeconds) {
  const mins = Math.round(totalSeconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/**
 * Primary forecast line. When the learner is on track to finish, show the
 * projected REVIEW time; when they're tracking to run out, show how many
 * questions would be left UNANSWERED at the current pace.
 */
function forecastHeadline(snapshot) {
  const unanswered = Math.round(snapshot.projectedUnanswered);
  if (unanswered >= 1) {
    const noun = unanswered === 1 ? 'question' : 'questions';
    return `≈ ${unanswered} ${noun} unanswered at this pace`;
  }
  if (snapshot.reviewBufferSeconds > 30) {
    return `≈ ${fmtDuration(snapshot.reviewBufferSeconds)} for review at this pace`;
  }
  return 'On pace to finish — minimal review time';
}

/** Muted secondary line: minutes ahead/behind schedule. */
function deltaLabel(deltaSeconds) {
  const mins = Math.round(Math.abs(deltaSeconds) / 60);
  if (mins < 1) return 'on pace';
  return deltaSeconds >= 0 ? `${mins} min ahead` : `${mins} min behind`;
}

/**
 * Compact confidence chip — used in the collapsed mobile timer bar. Mirrors the
 * chip the full card shows, so the learner keeps an at-a-glance status while the
 * verbose forecast is folded away.
 * @param {object} props
 * @param {object} props.snapshot  output of paceService.computePace()
 */
export function PaceChip({ snapshot }) {
  if (!snapshot || !snapshot.valid) return null;
  if (snapshot.warmup) {
    return <span className={`${css.chip} ${css.chipNeutral}`}>Establishing pace…</span>;
  }
  const conf = CONFIDENCE[snapshot.confidence] || CONFIDENCE.good;
  return (
    <span className={css.chip} style={{ color: conf.color, borderColor: conf.color }}>
      {conf.label}
    </span>
  );
}

/**
 * @param {object} props
 * @param {object} props.snapshot  output of paceService.computePace()
 */
export default function PaceCard({ snapshot }) {
  // No valid pacing info → render nothing (defense in depth; caller also gates).
  if (!snapshot || !snapshot.valid) return null;

  const { warmup, confidence, deltaSeconds } = snapshot;

  // Warmup: stay neutral. No forecast, no confidence color yet.
  if (warmup) {
    return (
      <div className={css.card} data-testid="pace-card" data-pace-state="warmup">
        <span className={`${css.chip} ${css.chipNeutral}`}>Establishing pace…</span>
      </div>
    );
  }

  const conf = CONFIDENCE[confidence] || CONFIDENCE.good;

  return (
    <div
      className={css.card}
      data-testid="pace-card"
      data-pace-state="active"
      data-pace-confidence={confidence}
    >
      <span className={css.headline}>{forecastHeadline(snapshot)}</span>
      <span className={css.chip} style={{ color: conf.color, borderColor: conf.color }}>
        {conf.label}
      </span>
      <span className={css.sub}>{deltaLabel(deltaSeconds)}</span>
    </div>
  );
}
